/**
 * FSE SearchBar — M3 Expressive docked search input.
 *
 * Design source: `Documentation/Claude design files/preview/search-bar.html`.
 * Spec: 56dp-tall filled field on a surfaceContainerHigh background, full pill
 * radius, a leading search icon and an optional trailing clear ("close") icon
 * that appears once there is text. Rendered as a borderless filled field — the
 * pill container carries the shape, not a TextField outline.
 *
 * Icons are inline Material Symbols (no sibling Icon import). Public prop
 * surface is FSE-owned; MUI's `sx` is never exposed.
 */
import {
  Box,
  IconButton,
  InputBase,
  type InputBaseProps,
} from '@mui/material';
import { forwardRef } from 'react';
import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, tap, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

export interface SearchBarProps {
  /** Current query text. */
  value: string;
  /** Fires with the next query text. */
  onChange: (value: string) => void;
  /** Placeholder text. Default "Search". */
  placeholder?: string;
  /** Called when the trailing clear button is pressed. If omitted, clear
   *  emits an empty string via `onChange` and no extra side-effect runs. */
  onClear?: () => void;
  /** Disable input. */
  disabled?: boolean;
  /** Focus on mount. */
  autoFocus?: boolean;
  /** Accessible label. Default mirrors the placeholder. */
  'aria-label'?: string;
}

export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(function SearchBar(
  { value, onChange, placeholder = 'Search', onClear, disabled = false, autoFocus = false, 'aria-label': ariaLabel },
  ref,
) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const handleChange: NonNullable<InputBaseProps['onChange']> = (e) => onChange(e.target.value);

  const handleClear = () => {
    if (onClear) onClear();
    else onChange('');
  };

  return (
    <Box
      role="search"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        height: tap.md, // 56dp
        maxWidth: 360,
        width: '100%',
        backgroundColor: sys.surfaceContainerHigh,
        borderRadius: `${radius.full}px`,
        padding: '0 8px 0 4px',
        opacity: disabled ? 0.38 : 1,
      }}
    >
      <Box
        sx={{
          width: 48,
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name="search" size={24} color={sys.onSurfaceVariant} />
      </Box>
      <InputBase
        inputRef={ref}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        inputProps={{ 'aria-label': ariaLabel ?? placeholder }}
        sx={{
          flex: 1,
          fontFamily: typography.fontFamily,
          fontSize: typography.bodyMedium.fontSize,
          color: sys.onSurface,
          '& input::placeholder': {
            color: sys.onSurfaceVariant,
            opacity: 1,
          },
        }}
      />
      {value.length > 0 && !disabled && (
        <IconButton
          onClick={handleClear}
          aria-label="Clear search"
          sx={{
            width: 48,
            height: 48,
            color: sys.onSurfaceVariant,
          }}
        >
          <Icon name="close" size={24} />
        </IconButton>
      )}
    </Box>
  );
});
